"use strict";
/**
 * Utility functions for building date and month dropdown options.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.monthValueToDisplayString = exports.parseMonthValue = exports.getCurrentMonthValue = exports.buildDayDropdownOptions = exports.buildMonthDropdownOptions = exports.getMonthString = exports.getDateString = void 0;
/**
 * Formats a date as "M/D/YYYY".
 */
function getDateString(date) {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}
exports.getDateString = getDateString;
/**
 * Formats a date as "Month YYYY" (e.g., "January 2026").
 */
function getMonthString(date) {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}
exports.getMonthString = getMonthString;
/**
 * Builds dropdown options for month selection.
 *
 * @param startDate - The earliest date to include (exclusive)
 * @param endDate - The latest date to include (defaults to now)
 * @returns Array of dropdown items with friendly names and "YYYY-MM" values
 */
function buildMonthDropdownOptions(startDate, endDate = new Date()) {
    const options = [];
    const monthDate = new Date(endDate);
    const startDateTime = new Date(startDate).getTime();
    while (monthDate.getTime() > startDateTime) {
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth() + 1;
        const monthValue = `${year}-${String(month).padStart(2, "0")}`;
        const monthString = getMonthString(monthDate);
        options.push({
            name: monthString,
            value: monthValue,
        });
        monthDate.setMonth(monthDate.getMonth() - 1);
    }
    return options;
}
exports.buildMonthDropdownOptions = buildMonthDropdownOptions;
/**
 * Builds dropdown options for day selection within a specific month.
 *
 * @param selectedMonth - Month in "YYYY-MM" format
 * @param startDate - The earliest date to include (exclusive)
 * @param endDate - The latest date to include (defaults to now)
 * @returns Array of dropdown items with labels and "M/D/YYYY" values
 */
function buildDayDropdownOptions(selectedMonth, startDate, endDate = new Date()) {
    const options = [];
    const [yearStr, monthStr] = selectedMonth.split("-");
    const selectedYear = parseInt(yearStr, 10);
    const selectedMonthNum = parseInt(monthStr, 10) - 1; // Convert to 0-indexed
    const startDateTime = new Date(startDate).getTime();
    const date = new Date(endDate);
    let i = 0;
    while (date.getTime() > startDateTime) {
        const dateYear = date.getFullYear();
        const dateMonth = date.getMonth();
        // Only include dates from the selected month.
        if (dateYear === selectedYear && dateMonth === selectedMonthNum) {
            const dateString = getDateString(date);
            let label = dateString;
            if (i === 0) {
                label = "Today";
            }
            else if (i === 1) {
                label = "Yesterday";
            }
            options.push({
                name: label,
                value: dateString,
            });
        }
        date.setDate(date.getDate() - 1);
        i++;
    }
    return options;
}
exports.buildDayDropdownOptions = buildDayDropdownOptions;
/**
 * Gets the current month in "YYYY-MM" format.
 */
function getCurrentMonthValue(date = new Date()) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}
exports.getCurrentMonthValue = getCurrentMonthValue;
/**
 * Parses a month value in "YYYY-MM" format and returns year and month.
 *
 * @param monthValue - Month in "YYYY-MM" format
 * @returns Object with year and month (1-indexed)
 */
function parseMonthValue(monthValue) {
    const [yearStr, monthStr] = monthValue.split("-");
    return {
        year: parseInt(yearStr, 10),
        month: parseInt(monthStr, 10),
    };
}
exports.parseMonthValue = parseMonthValue;
/**
 * Converts a month value to display string.
 *
 * @param monthValue - Month in "YYYY-MM" format
 * @returns Display string like "January 2026"
 */
function monthValueToDisplayString(monthValue) {
    const { year, month } = parseMonthValue(monthValue);
    return getMonthString(new Date(year, month - 1, 1));
}
exports.monthValueToDisplayString = monthValueToDisplayString;
